import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Link,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { authApi } from "../apis/authApi";
import { useNinjaOneRefreshToken } from "../hooks/useNinjaOneRefreshToken";
import DattoScriptExportDialog from "./DattoScriptExportDialog";
import { getNinjaOneRedirectUri } from "../utils/ninjaOneOAuth";

interface NinjaOneSetupFormProps {
  onBack?: () => void;
  onSuccess?: () => void;
  onSkip?: () => void;
}

const NinjaOneSetupForm: React.FC<NinjaOneSetupFormProps> = ({ onBack, onSuccess, onSkip }) => {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [instanceUrl, setInstanceUrl] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  
  const testedValuesRef = useRef<{
    clientId: string;
    clientSecret: string;
    instanceUrl: string;
    refreshToken: string;
  } | null>(null);
  
  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    return (
      clientId !== testedValuesRef.current.clientId ||
      clientSecret !== testedValuesRef.current.clientSecret ||
      instanceUrl !== testedValuesRef.current.instanceUrl ||
      refreshToken !== testedValuesRef.current.refreshToken
    );
  };
  
  const callbackUrl = getNinjaOneRedirectUri();

  const { isGettingRefreshToken, startSignIn: handleStartNinjaOneSignIn } = useNinjaOneRefreshToken({
    instanceUrl,
    clientId,
    clientSecret,
    contextKey: "ninjaone-setup",
    onStart: () => {
      setTestResult(null);
      testedValuesRef.current = null;
    },
    onSuccess: (token) => {
      setRefreshToken(token);
      setTestResult({ success: true, message: "Refresh Token added. Click Test Connection to verify NinjaOne." });
    },
    onError: (message) => setTestResult({ success: false, message }),
  });
  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving || isTesting || isGettingRefreshToken;

  const handleTestConnection = async () => {
    if (!clientId || !clientSecret || !instanceUrl || !refreshToken) {
      setTestResult({
        success: false,
        message: "Please fill in the instance URL, client ID, client secret, and refresh token before testing.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await authApi.testNinjaOneConnection({
        client_id: clientId,
        client_secret: clientSecret,
        instance_url: instanceUrl,
        refresh_token: refreshToken,
      });

      if (result.success) {
        testedValuesRef.current = {
          clientId,
          clientSecret,
          instanceUrl,
          refreshToken,
        };
      }

      setTestResult({
        success: result.success,
        message: result.message || "Connection test successful!",
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      await authApi.saveNinjaOneConfiguration({
        client_id: clientId,
        client_secret: clientSecret,
        instance_url: instanceUrl,
        refresh_token: refreshToken,
      });

      setShowSuccessSnackbar(true);
      
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to save NinjaOne configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {onBack && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              background: "#f5f5f5",
            },
          }}
        >
          Back
        </Button>
      )}

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        NinjaOne Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          How to Connect NinjaOne
        </Typography>
        <Box
          component="ol"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            pl: 3,
            "& li": {
              mb: 1.5,
              lineHeight: 1.6,
            },
          }}
        >
          <li>
            Log in to your NinjaOne account as an administrator.
          </li>
          <li>
            Navigate to <strong>Administration &gt; Apps &gt; API</strong>.
          </li>
          <li>Open Client app IDs, then click Add client app.</li>
          <li>In Application platform, choose Web.</li>
          <li>Set Name to something recognizable, such as Theo AI.</li>
          <li>
            Add this Redirect URI:{" "}
            <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>
              {callbackUrl}
            </Typography>
            .
          </li>
          <li>
            In Scopes, check:
            <Box component="ol" sx={{ mt: 0.75, pl: 0, listStyle: "none" }}>
              <li>6.1 Monitoring: read-only device and organization data.</li>
              <li>6.2 Management: NinjaOne script runs and Theo secure shell bootstrap scripts.</li>
            </Box>
          </li>
          <li>Check Authorization code and Refresh token.</li>
          <li>Click Get Refresh Token in Theo and sign in with a technician account that can run automations.</li>
          <li>
            Confirm the fields below have the <strong>Instance URL</strong>, <strong>Client ID</strong>,{" "}
            <strong>Client Secret</strong>, and <strong>Refresh Token</strong>.
          </li>
          <li>
            For more information, visit the{" "}
            <Link
              href="https://www.ninjaone.com/docs/integrations/how-to-set-up-api-oauth-token/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "#705d4a" }}
            >
              NinjaOne API token setup guide
            </Link>
            .
          </li>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#fff",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 1,
            color: "#1a1a1a",
          }}
        >
          Theo Scripts
        </Typography>
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#6c6c6c", mb: 2, lineHeight: 1.6 }}>
          Export NinjaOne scripts for Theo Windows/macOS secure shell and script actions.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
            sx={{ fontFamily: "var(--theo-font-sans)", textTransform: "none", borderColor: "#705d4a", color: "#705d4a" }}
          >
            Export
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Instance URL"
          disabled={isGettingRefreshToken || isTesting || isSaving}
          value={instanceUrl}
          onChange={(e) => {
            setInstanceUrl(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="https://app.ninjarmm.com"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your NinjaOne instance URL"
        />

        <TextField
          label="Client ID"
          disabled={isGettingRefreshToken || isTesting || isSaving}
          value={clientId}
          onChange={(e) => {
            setClientId(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="your-client-id"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Client ID from the NinjaOne Web client app"
        />

        <TextField
          label="Client Secret"
          disabled={isGettingRefreshToken || isTesting || isSaving}
          type="password"
          value={clientSecret}
          onChange={(e) => {
            setClientSecret(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Client secret from the same NinjaOne Web client app"
        />

        <TextField
          label="Refresh Token"
          disabled={isGettingRefreshToken || isTesting || isSaving}
          type="password"
          value={refreshToken}
          onChange={(e) => {
            setRefreshToken(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Use NinjaOne sign-in to fill this, or paste an existing Refresh Token"
        />

        <Box sx={{ mt: -2, display: "flex", justifyContent: "flex-start" }}>
          <Button
            variant="outlined"
            onClick={handleStartNinjaOneSignIn}
            disabled={isGettingRefreshToken || isTesting || isSaving || !instanceUrl.trim() || !clientId.trim() || !clientSecret.trim()}
            sx={{ fontFamily: "var(--theo-font-sans)", textTransform: "none", borderColor: "#705d4a", color: "#705d4a" }}
          >
            {isGettingRefreshToken ? "Waiting for NinjaOne..." : "Get Refresh Token"}
          </Button>
        </Box>

        {testResult && (
          <Alert
            severity={testResult.success ? "success" : "error"}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          >
            {testResult.message}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || isSaving || isGettingRefreshToken || !clientId || !clientSecret || !instanceUrl || !refreshToken}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 150,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>

          {onSkip && (
            <Button
              variant="text"
              onClick={onSkip}
              sx={{
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
                fontWeight: 500,
                color: "#6c6c6c",
                "&:hover": {
                  background: "#f5f5f5",
                },
              }}
            >
              Skip for now
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          NinjaOne has been successfully connected!
        </Alert>
      </Snackbar>
      <DattoScriptExportDialog
        provider="ninjaone"
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </Box>
  );
};

export default NinjaOneSetupForm;
